import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleService } from '@/services/roleService';
import { Role, CreateRolePayload, QUERY_KEYS } from '@/types';
import { toast } from 'sonner';
import { AxiosError } from 'axios';

interface ErrorResponse {
  detail?: string;
  title?: string;
  [key: string]: unknown;
}

/**
 * Hook to fetch all roles
 */
export const useRoles = () => {
  return useQuery({
    queryKey: QUERY_KEYS.roles,
    queryFn: () => roleService.getAll().then((res) => res.data),
    staleTime: 10 * 60 * 1000, // 10 minutes (roles change infrequently)
  });
};

/**
 * Hook to fetch a single role by id
 */
export const useRole = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.role(id),
    queryFn: () => roleService.getById(id).then((res) => res.data),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to check role name availability (debounced in component)
 */
export const useCheckRoleNameAvailability = (name: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: QUERY_KEYS.roleNameAvailability(name),
    queryFn: () => roleService.checkNameAvailability(name).then((res) => res.data),
    enabled: enabled && name.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    retry: false,
  });
};

/**
 * Hook to create a new role (Admin only)
 */
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateRolePayload) => roleService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles });
      toast.success('Role created successfully');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.response?.data ||
        'Failed to create role';
      toast.error(typeof message === 'string' ? message : 'Failed to create role');
    },
  });
};

/**
 * Hook to delete a role (Admin only)
 * Backend returns 409 Conflict if role is assigned to users
 */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => roleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles });
      toast.success('Role deleted successfully');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      // Handle 409 Conflict specifically for role assignment warning
      if (error.response?.status === 409) {
        toast.error(
          'Cannot delete role: It is currently assigned to one or more users. Please reassign users before deleting this role.',
          { duration: 6000 }
        );
      } else {
        const message =
          error.response?.data?.detail ||
          error.response?.data?.title ||
          error.response?.data ||
          'Failed to delete role';
        toast.error(typeof message === 'string' ? message : 'Failed to delete role');
      }
    },
  });
};
